import { useMemo } from 'react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useBootstrapUsuario, useRealtimeBootstrap } from '@/hooks/useBootstrapUsuario';
import { resolverIcone } from '@/utils/iconRegistry';

const comparadorMenu = new Intl.Collator('pt-BR', {
  sensitivity: 'base',
  numeric: true,
});

export interface MenuItemDinamico {
  id: string;
  label: string;
  icon: LucideIcon;
  iconNome: string;
  path: string;
  dynamicCheck: string | null;
}

export interface MenuGrupoDinamico {
  label: string | null;
  ordem: number;
  items: MenuItemDinamico[];
}

/**
 * Lê o menu já vindo do bootstrap unificado (sem novo round-trip).
 * O filtro por roles é feito no Postgres dentro de `fn_bootstrap_usuario`.
 */
export const useMenuDinamico = () => {
  const { user, rolesLoading } = useAuth();
  const query = useBootstrapUsuario(user?.id);
  useRealtimeBootstrap(user?.id);

  const grupos = useMemo<MenuGrupoDinamico[]>(() => {
    const rows = query.data?.menu_items ?? [];
    const linhasOrdenadas = [...rows].sort((a, b) =>
      a.ordem_grupo - b.ordem_grupo || comparadorMenu.compare(a.label, b.label),
    );

    const mapa = new Map<string, MenuGrupoDinamico>();
    for (const row of linhasOrdenadas) {
      const chave = row.grupo ?? '__principal__';
      if (!mapa.has(chave)) {
        mapa.set(chave, {
          label: row.grupo,
          ordem: row.ordem_grupo,
          items: [],
        });
      }
      mapa.get(chave)!.items.push({
        id: row.id,
        label: row.label,
        icon: resolverIcone(row.icon),
        iconNome: row.icon,
        path: row.path,
        dynamicCheck: row.dynamic_check,
      });
    }
    return Array.from(mapa.values()).map((grupo) => ({
      ...grupo,
      items: [...grupo.items].sort((a, b) => comparadorMenu.compare(a.label, b.label)),
    }));
  }, [query.data]);

  return {
    grupos,
    isLoading: query.isLoading || rolesLoading,
    isError: query.isError,
  };
};
